/**
 * Notification Type Constants
 * 
 * This file defines all notification types used throughout the application.
 * All notification types use snake_case format.
 * 
 * IMPORTANT: Always use these constants instead of string literals to ensure consistency
 * and prevent typos. TypeScript will catch errors at compile time.
 */

export const NOTIFICATION_TYPES = {
  // Partner Request Notifications
  UNIFIED_PARTNER_REQUEST: 'unified_partner_request',
  PARTNER_REQUEST: 'partner_request',
  
  // Carrier Action Notifications
  CARRIER_ACCEPT: 'carrier_accept',
  CARRIER_ACCEPT_BROKER: 'carrier_accept_broker',
  CARRIER_DECLINE: 'carrier_decline',
  CARRIER_COUNTER_OFFER: 'carrier_counter_offer',
  MAKE_OFFER: 'make_offer',
  /** Phase 4B: Shipper/broker approved marketplace carrier; carrier must confirm rate confirmation to complete assign + add partner */
  MARKETPLACE_APPROVAL_RATE_CONFIRMATION: 'marketplace_approval_rate_confirmation',
  
  // Load Status Notifications
  LOAD_STARTED: 'load_started',           // Carrier started load (Phase 4 E2E)
  LOAD_COMPLETED: 'load_completed',
  LOAD_OFF_ROUTE: 'load_off_route',       // Optional: carrier detected off route (Phase 4)
  LOAD_STATUS_UPDATE: 'load_status_update',
  LOAD_MODIFIED: 'load_modified',
  LOAD_CANCELLED: 'load_cancelled',
  
  // Cancellation Workflow (two-party + TONU)
  CANCELLATION_REQUEST: 'cancellation_request',
  CANCELLATION_APPROVED: 'cancellation_approved',
  CANCELLATION_REJECTED: 'cancellation_rejected',
  TONU_INVOICE_CREATED: 'tonu_invoice_created',
  
  // System Notifications
  SYSTEM_ALERT: 'system_alert',
  RATING_RECEIVED: 'rating_received',
  MESSAGE_RECEIVED: 'message_received',
  MARKETPLACE: 'marketplace',

  /** Payer submitted check; carrier should confirm in Payments */
  CHECK_PAYMENT_PENDING: 'check_payment_pending'
} as const;

/**
 * Notification Status Constants
 * Used in the `status` field of notifications
 */
export const NOTIFICATION_STATUS = {
  PENDING: 'pending',
  ACCEPTED: 'accepted',
  REJECTED: 'rejected', // Used for notification status (carrier rejected load)
  COUNTER_OFFER: 'counter_offer'
} as const;

/**
 * Partner Request Status Constants
 * Used in the `status` field of partnerRequests collection
 * NOTE: This is different from notification status - 'declined' is for partner requests, 'rejected' is for notifications
 */
export const PARTNER_REQUEST_STATUS = {
  PENDING: 'pending',
  ACCEPTED: 'accepted',
  DECLINED: 'declined', // Used for partner request status (request was declined)
  REJECTED: 'rejected', // Used for partner request status (request was rejected)
  CANCELLED: 'cancelled',
  OFFERED: 'offered'
} as const;

/**
 * Type exports for TypeScript type safety
 */
export type NotificationType = typeof NOTIFICATION_TYPES[keyof typeof NOTIFICATION_TYPES];
export type NotificationStatus = typeof NOTIFICATION_STATUS[keyof typeof NOTIFICATION_STATUS];
export type PartnerRequestStatus = typeof PARTNER_REQUEST_STATUS[keyof typeof PARTNER_REQUEST_STATUS];
